// backoffice.settings.location.business-profile (SCR-125; field note backoffice.settings.location.business-profile;
// parity settings-location.md §1.1; SET-LOC-002..004): the location's tax rate, colour, address and contact. Each is a
// location setting (`pricing.tax.default_rate`, `location.profile.*`), so the register's receipts print the address and
// phone as they do every other location setting. Save writes only the changed keys.
// FIX (bugs.md): legacy showed "City is required" and saved anyway (blanking the city); Save here refuses while a
// required field is empty.
// Radish staff and resellers (`settings.platform.manage`) also edit the name, the time zone and the email: their Save
// sends `tenancy.updateLocation` (plan 2026-10-12-accounts-and-locations §4, one rule set with the staff dialog, A40 (b)),
// and a time zone change asks first. Merchants see those three read-only, as before. Phone is required (A40 (b)).
import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LEGACY_ONLY_LOCATION_COLORS, LOCATION_COLORS, type SettingKey } from '@radish/contracts';
import { Button, LoadingState, ScaledField, SelectField, SettingRow, SettingsPage, TextField, useToast } from '@radish/ui-web';
import { useActiveLocation } from '../../app/location.tsx';
import { useWritable } from '../../env/availability.tsx';
import { unwrap, useApi } from '../../data/api.tsx';
import { encodeSetting, useEffectiveSettings } from '../../data/settings.ts';
import { uuidv7 } from '../../lib/ids.ts';
import { refreshSession } from '../../session/session.ts';
import { ConfirmDialog } from '../users/UsersPage.tsx';
import { rateFromTenThousandths, tenThousandthsOf } from '../../lib/rates.ts';
import { PHONE_FULL_WIDTH, PageTabs } from '../../shell/page.tsx';
import { SelectColor } from '../../shell/select-color.tsx';
import { useSetSetting } from '../settings/GeneratedSettingsPage.tsx';
import { BUSINESS_PROFILE_TABS } from './tabs.ts';
import { EMAIL_INVALID, EMAIL_PATTERN, EMAIL_REQUIRED } from '../../lib/email.ts';
import { DESCRIPTION_TITLE_INK } from '../../shell/ui-description.ts';

type TextKey =
  | 'location.profile.address_line1' | 'location.profile.city' | 'location.profile.state' | 'location.profile.zip' | 'location.profile.phone'
  | 'location.profile.contact_first_name' | 'location.profile.contact_last_name';
type Color = (typeof LOCATION_COLORS)[number] | (typeof LEGACY_ONLY_LOCATION_COLORS)[number];

const TEXT_KEYS: readonly TextKey[] = [
  'location.profile.address_line1', 'location.profile.city', 'location.profile.state', 'location.profile.zip', 'location.profile.phone',
  'location.profile.contact_first_name', 'location.profile.contact_last_name',
];

/** Legacy's required fields and their messages (BusinessProfile.vue rules). */
const REQUIRED: Readonly<Partial<Record<TextKey, string>>> = {
  'location.profile.address_line1': 'Address is required',
  'location.profile.city': 'City is required',
  'location.profile.state': 'State is required',
  'location.profile.zip': 'Zip is required',
  'location.profile.contact_first_name': 'Contact First Name is required',
  'location.profile.contact_last_name': 'Contact Last Name is required',
  'location.profile.phone': 'Phone is required',
};

const ZONES = ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'Pacific/Honolulu'] as const;
type Zone = (typeof ZONES)[number];

/** Legacy SelectTimezone.vue's zones and names. */
const ZONE_NAMES: Readonly<Record<string, string>> = {
  'America/Los_Angeles': 'Pacific Time', 'America/Denver': 'Mountain Time', 'America/Chicago': 'Central Time', 'America/New_York': 'Eastern Time',
  'Pacific/Honolulu': 'Hawaii-Aleutian Time',
};

/** "(GMT -05:00) Central Time", as legacy labels a zone (its offset today). */
export function zoneLabel(zone: string, at: Date = new Date()): string {
  const offset = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const hhmm = offset === 'GMT' ? '+00:00' : offset.slice(3);
  return `(GMT ${hhmm}) ${ZONE_NAMES[zone] ?? zone}`;
}

/** A phone's digits as legacy's PhoneInput shows them: (512) 555-0202. */
export function phoneText(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length <= 3) return d.length === 0 ? '' : `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

interface Form {
  readonly text: Readonly<Record<TextKey, string>>;
  readonly tax: bigint;
  readonly color: Color;
  /** Staff only: the tenancy name and zone, and the email. */
  readonly name: string;
  readonly timezone: string;
  readonly email: string;
}

type Errors = Partial<Record<TextKey | 'name' | 'email', string>>;

/**
 * Legacy SelectColor in its dashed `ui-bordered-input` row (padding 4 4 4 10): Location Info's Location Color, and Profile's
 * Profile Color. The trigger sits 5 px in from the row's right edge.
 */
export function ColorPicker<C extends string>({ value, onChange, disabled, label = 'Location Color', options = LOCATION_COLORS as unknown as readonly C[] }: {
  readonly value: C; readonly onChange: (c: C) => void; readonly disabled: boolean; readonly label?: string; readonly options?: readonly C[];
}) {
  return (
    <div className="flex h-14 items-center justify-between rounded-q border border-dashed border-line bg-field py-1 pr-1 pl-2.5">
      <span className={`font-medium ${DESCRIPTION_TITLE_INK}`}>{label}</span>
      <SelectColor value={value} onChange={onChange} disabled={disabled} label={label} options={options} />
    </div>
  );
}

const Grid = ({ children }: { readonly children: ReactNode }) => <div className="grid grid-cols-1 gap-4 min-[600px]:grid-cols-2">{children}</div>;

export function LocationInfoPage() {
  const location = useActiveLocation();
  const toast = useToast();
  const effective = useEffectiveSettings(location.locationId);
  const setSetting = useSetSetting(location.locationId);
  const writable = useWritable('any');
  const staff = location.isPlatformAdmin;
  const { client, environment } = useApi();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState<Form | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const [confirmZone, setConfirmZone] = useState(false);

  useEffect(() => {
    const s = effective.settings;
    if (s === undefined) return;
    const loaded: Form = {
      text: Object.fromEntries(TEXT_KEYS.map((k) => [k, s.get(k)])) as Record<TextKey, string>,
      tax: tenThousandthsOf(s.get('pricing.tax.default_rate')),
      color: s.get('location.profile.color'),
      name: location.name,
      timezone: location.timezone,
      email: s.get('location.profile.email'),
    };
    setSaved(loaded);
    setForm(loaded);
  }, [effective.settings, location.name, location.timezone]);

  if (effective.isPending || form === null || saved === null) return <LoadingState />;

  const setText = (key: TextKey, value: string) => {
    setForm({ ...form, text: { ...form.text, [key]: value } });
    if (errors[key] !== undefined && value.trim() !== '') setErrors({ ...errors, [key]: undefined });
  };

  const identityChanged = staff && (form.name.trim() !== saved.name || form.timezone !== saved.timezone || form.email.trim().toLowerCase() !== saved.email);

  /** Staff: one `tenancy.updateLocation` with every detail (the name, zone and email live in it), then the colour. */
  const saveAsStaff = async () => {
    setConfirmZone(false);
    setSaving(true);
    try {
      const t = form.text;
      await unwrap(() => client.commands.tenancy.updateLocation({
        locationId: location.locationId as never, name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: t['location.profile.phone'], taxRate: rateFromTenThousandths(form.tax),
        timezone: form.timezone as Zone, addressLine1: t['location.profile.address_line1'].trim(), city: t['location.profile.city'].trim(), state: t['location.profile.state'].trim(),
        zip: t['location.profile.zip'].trim(), contactFirstName: t['location.profile.contact_first_name'].trim(), contactLastName: t['location.profile.contact_last_name'].trim(),
      }, { commandId: uuidv7() }));
      if (form.color !== saved.color) await setSetting.mutateAsync({ key: 'location.profile.color', value: encodeSetting('location.profile.color', form.color) });
      await queryClient.invalidateQueries({ queryKey: [environment.profile, location.locationId] });
      // The shell's name and zone come from the session's location list.
      await refreshSession();
      toast.success('Location updated successfully');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const missing: Errors = {
      ...Object.fromEntries(Object.entries(REQUIRED).filter(([k]) => form.text[k as TextKey].trim() === '')),
      ...(staff && form.name.trim() === '' ? { name: 'Location Name is required' } : {}),
      ...(identityChanged && !EMAIL_PATTERN.test(form.email.trim()) ? { email: form.email.trim() === '' ? EMAIL_REQUIRED : EMAIL_INVALID } : {}),
    };
    setErrors(missing);
    if (Object.keys(missing).length > 0) return;
    if (identityChanged) {
      if (form.timezone !== saved.timezone) setConfirmZone(true);
      else await saveAsStaff();
      return;
    }
    const writes: [SettingKey, unknown][] = [];
    for (const k of TEXT_KEYS) {
      const value = form.text[k].trim();
      if (value !== saved.text[k]) writes.push([k, encodeSetting(k, value)]);
    }
    if (form.tax !== saved.tax) writes.push(['pricing.tax.default_rate', encodeSetting('pricing.tax.default_rate', rateFromTenThousandths(form.tax))]);
    if (form.color !== saved.color) writes.push(['location.profile.color', encodeSetting('location.profile.color', form.color)]);
    setSaving(true);
    try {
      for (const [key, value] of writes) await setSetting.mutateAsync({ key, value });
      toast.success('Location updated successfully');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key: TextKey, label: string) => (
    <TextField label={label} value={form.text[key]} onValueChange={(v) => setText(key, v)} error={errors[key]} disabled={!writable} />
  );

  return (
    <div data-testid="location-info-page">
      <PageTabs label="Business Profile" tabs={BUSINESS_PROFILE_TABS} />
      <SettingsPage
        top="tabs"
        intro="Add and/or edit the information of your location, including the name, default tax rate, time zone, address, and contact information."
        actions={<Button onClick={() => void save()} loading={saving} disabled={!writable} className={PHONE_FULL_WIDTH}>Save</Button>}
      >
        <SettingRow heading="Location Info" first>
          <div className="flex flex-col gap-4">
            <Grid>
              <TextField
                label="Location Name" value={form.name} disabled={!staff || !writable} error={errors.name}
                onValueChange={(v) => setForm({ ...form, name: v })} hint="Note that the location name appears on your reports and customer receipts."
              />
              <ScaledField label="Tax Rate" suffix="%" decimals={4} maxDigits={7} value={form.tax} disabled={!writable} onValueChange={(v) => setForm({ ...form, tax: v ?? 0n })} />
            </Grid>
            <ColorPicker value={form.color} onChange={(color) => setForm({ ...form, color })} disabled={!writable} />
          </div>
        </SettingRow>
        <SettingRow heading="Time Zone">
          <Grid>
            {staff ? (
              <SelectField label="Time Zone" value={form.timezone as Zone} options={ZONES.map((z) => ({ value: z, label: zoneLabel(z) }))} onValueChange={(z) => setForm({ ...form, timezone: z })} disabled={!writable} testId="location-info-timezone" />
            ) : (
              <TextField label="Time Zone" value={zoneLabel(location.timezone)} onValueChange={() => undefined} disabled />
            )}
          </Grid>
        </SettingRow>
        <SettingRow heading="Address">
          <div className="flex flex-col gap-4">
            {field('location.profile.address_line1', 'Address')}
            {/* Legacy's City is col-xs-12 col-sm-6 and State and Zip col-xs-6 col-sm-3: a four-column row from 600 px. */}
            <div className="grid grid-cols-2 gap-4 min-[600px]:grid-cols-4" data-testid="city-state-zip">
              <div className="col-span-2">{field('location.profile.city', 'City')}</div>
              {field('location.profile.state', 'State')}
              {field('location.profile.zip', 'Zip')}
            </div>
          </div>
        </SettingRow>
        <SettingRow heading="Contact Info">
          <Grid>
            {field('location.profile.contact_first_name', 'Contact First Name')}
            {field('location.profile.contact_last_name', 'Contact Last Name')}
            <TextField label="Email" value={form.email} onValueChange={(v) => setForm({ ...form, email: v })} disabled={!staff || !writable} error={errors.email} />
            <TextField
              label="Phone Number" inputMode="tel" value={phoneText(form.text['location.profile.phone'])} disabled={!writable} error={errors['location.profile.phone']}
              onValueChange={(v) => setText('location.profile.phone', v.replace(/\D/g, '').slice(0, 10))}
            />
          </Grid>
        </SettingRow>
      </SettingsPage>
      <ConfirmDialog
        open={confirmZone}
        title="Change Time Zone"
        message={`Change the time zone to ${zoneLabel(form.timezone)}? Business days from the next day's start follow the new zone; earlier days keep theirs.`}
        action="Change Time Zone"
        busy={saving}
        onClose={() => setConfirmZone(false)}
        onConfirm={() => void saveAsStaff()}
      />
    </div>
  );
}
